/**
 * @file styliticsInsightsAnalytics.js
 * @description
 * This script integrates Stylitics Insights analytics into the Complete Look widget on the site.
 * It loads the Stylitics Insights script dynamically, initializes analytics tracking, and sets up event listeners
 * to track user interactions such as widget load, adding items to cart, item clicks, and item views within the widget.
 *
 * Main usage:
 * - Dynamically loads the Stylitics Insights analytics script based on provided settings.
 * - Initializes the StyliticsInsights instance when the Complete Look widget is shown in the minicart.
 * - Tracks and sends analytics events to Stylitics for user interactions within the Complete Look widget.
 * - Ensures analytics are only initialized if settings are present and the Stylitics script is available.
 */
'use strict';

import { queryFirst } from 'lilly/domUtil';
const styliticsSettingsContainer = queryFirst('#stylitics-analytics-settings');
const ITEM_IMAGE_TYPE = 'client_original_image';
const miniCartSheetContainer = queryFirst('#minicart');
let isMiniToteInsightsInitialized = false;
let isPDPInsightsInitialized = false;

let trackingQueue = Promise.resolve();
/**
 * Enqueues a tracking task to be executed in order
 * And to prevent blocking the main thread
 * @param {*} task - The tracking task to be executed.
 */
function enqueueTracking(task) {
    trackingQueue = trackingQueue
        .then(() => task())
        .catch(err => window.console.error('Stylitics tracking error:', err));
}

/**
 * get stylitics analytics settings
 * @return {Object|boolean} stylitics analytics settings
 */
function getSettings() {
    if (!styliticsSettingsContainer) return false;

    const { styliticsAnalyticsSettings } = styliticsSettingsContainer.dataset;
    if (!styliticsAnalyticsSettings) return false;

    let settings;
    try {
        settings = JSON.parse(styliticsAnalyticsSettings);
    } catch (e) {
        return false;
    }

    return settings;
}

/**
 * get data for initialize widget
 * @param {Object} settings - widget settings
 * @param {string} styliticsRequestedId - requested product id for the widget
 * @return {Object} widget settings
 */
function getRequestData(settings, styliticsRequestedId) {
    if (!settings || !styliticsRequestedId) return null;
    const payload = {
        client: settings.clientID,
        env: settings.trackingEnv,
        itemId: styliticsRequestedId,
        placement: settings.placement
    };

    return { payload };
}

/**
 * main function to initialize StyliticsInsights instance and set up event listeners
 * @param {Object} styliticsInsights - widget settings
 * @param {HTMLElement} styliticsCompleteLookContainer - container of the Stylitics Complete Look widget
 */
function initializeStyliticsAnalyticsEvent(styliticsInsights, styliticsCompleteLookContainer) {
    // Send add item to cart event to Stylitics: on click to "Add to Cart" button in Complete Look widget
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:addItemToCart', (event) => {
        const data = event && event.detail;
        if (!styliticsInsights || !data || !data.pid) return;

        const analyticsData = {
            itemId: data.pid,
            placement: 'widget'
        };
        if (data.price) analyticsData.price = data.price.split('$').pop();

        enqueueTracking(async () => {
            await styliticsInsights.trackAddItemToCart(analyticsData);
        });
    });

    // Send item click event to Stylitics:
    // on click to "View Full Details" button inside Complete Look widget
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackItemClick', (event) => {
        const data = event && event.detail;
        if (!styliticsInsights || !data || !data.pid) return;

        const analyticsData = {
            item: {
                remote_id: data.pid,
                item_id: Number(data.itemId)
            },
            uiComponent: 'widget',
            placement: data.placement || styliticsInsights.placement,
            position: data.index || 0
        };
        // send trackItemClick data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            styliticsInsights.trackClickItem(analyticsData);
        });
    });

    // Send item view event to Stylitics: on tab change in Complete Look widget
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackItemView', (event) => {
        const data = event && event.detail;
        if (!styliticsInsights || !data || !data.pid) return;

        const analyticsData = {
            item: {
                remote_id: data.pid,
                item_id: Number(data.itemId)
            },
            outfitId: data.outfitId ? Number(data.outfitId) : null,
            itemImageType: ITEM_IMAGE_TYPE,
            position: data.index || 0
        };
        // send trackItemView data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackViewItem(analyticsData);
        });
    });

    // Send Outfit view event to Stylitics
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackViewOutfit', e => {
        const data = e && e.detail;
        if (!styliticsInsights || !data || !data.outfitId) return;

        const analyticsData = {
            outfit: {
                id: Number(data.outfitId),
                primary_requested_id: data.pid,
                primary_remote_id: data.pid,
                username: styliticsInsights.client
            },
            uiComponent: data.uiComponent || 'widget',
            outfitPosition: data.index || 0
        };
        // send trackViewOutfit data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackViewOutfit(analyticsData);
        });
    });
    // Send Outfit click event to Stylitics:
    // on click an outfit/bundle inside the Custom Widget (any product image inside the bundle)
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackClickOutfit', (e) => {
        if (!styliticsInsights || !e.detail || !e.detail.outfitId) return;
        const data = e.detail;
        const analyticsData = {
            outfit: {
                id: Number(data.outfitId),
                primary_requested_id: data.pid,
                primary_remote_id: data.pid,
                username: styliticsInsights.client
            },
            uiComponent: data.uiComponent || 'call-to-action',
            outfitPosition: data.index || 0
        };
        // send trackClickOutfit data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackClickOutfit(analyticsData);
        });
    });

    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:updateStyliticsInsightsData', (e) => {
        const data = e && e.detail;
        if (!styliticsInsights || !data || !data.pid) return;
        if (styliticsInsights.itemId === data.pid) return;
        // update itemId in the StyliticsInsights instance
        styliticsInsights.itemId = data.pid;
        // Track widget load again after itemId update
        styliticsInsights.trackWidgetLoad();
    });
}
/**
 * Create a StyliticsInsights instance
 * @param {*} settings - widget settings
 *  @param {boolean} updateStyliticsInsightsData - flag to track if browsed item event was sent
 */
async function createStyliticsInsightsInstance(settings, updateStyliticsInsightsData) {
    if (!settings || !settings.script) return;
    let styliticsCompleteLookContainer;
    // Determine the container based on placement
    if (settings.placement === 'mini-cart') {
        styliticsCompleteLookContainer = miniCartSheetContainer;
    } else if (settings.placement === 'pdp') {
        styliticsCompleteLookContainer = queryFirst('#stylitics-pdp-api-widget');
    }

    if (!styliticsCompleteLookContainer) return;

    let { styliticsRequestedId } = styliticsCompleteLookContainer.dataset || {};
    if (!styliticsRequestedId) {
        const miniCartStyliticsContainer = queryFirst('#stylitics-complete-look', styliticsCompleteLookContainer);
        styliticsRequestedId = miniCartStyliticsContainer.dataset?.styliticsRequestedId;
    }
    // Stylitics is already initialized for this placement
    // But the widget data was updated based on changes in the cart or PDP
    // UUpdate the itemId in the existing StyliticsInsights instance and track widget load
    if (updateStyliticsInsightsData) {
        styliticsCompleteLookContainer.dispatchEvent(new CustomEvent('stylitics:completeLook:updateStyliticsInsightsData', {
            detail: {
                pid: styliticsRequestedId
            }
        }));
        return;
    }
    // Get request data for initializing StyliticsInsights
    let requestData = getRequestData(settings, styliticsRequestedId);
    // Use the asynchronous create method to create a new instance of StyliticsInsights
    // to track events inside the Complete Look widget
    if (!requestData || !requestData.payload || !window.StyliticsInsights) return;
    const styliticsInsights = await window.StyliticsInsights.create(requestData.payload);
    // Track widget load
    if (styliticsInsights) {
        if (settings.placement === 'mini-cart') isMiniToteInsightsInitialized = true;
        if (settings.placement === 'pdp') isPDPInsightsInitialized = true;
        // Initialize event listeners to track user interactions within the Complete Look widget
        initializeStyliticsAnalyticsEvent(styliticsInsights, styliticsCompleteLookContainer);
        // send trackClickOutfit data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackWidgetLoad();
        });
    }
}

// Load stylitics script and initialize the StyliticsInsights instance

document.addEventListener('DOMContentLoaded', () => {
    const settings = getSettings();
    if (!settings || !settings.script) return;
    import(/* webpackIgnore: true */ settings.script)
        .then(StyliticsInsights => {
            window.StyliticsInsights = StyliticsInsights.default;
            window.console.log('Stylitics Analytics: Script is loaded');
            // Initialize the StyliticsInsights instance for the mini cart
            miniCartSheetContainer.addEventListener('stylitics:minicart:show', () => {
                settings.placement = 'mini-cart';
                createStyliticsInsightsInstance(settings, isMiniToteInsightsInitialized);
            });
            // Initialize the StyliticsInsights instance for the PDP
            document.body.addEventListener('stylitics:pdp:widget:loaded', () => {
                settings.placement = 'pdp';
                createStyliticsInsightsInstance(settings, isPDPInsightsInitialized);
            });
            // Mark the Stylitics script is loaded to make sure that all event listeners are added and ready to use
            styliticsSettingsContainer.dataset.isStyliticsScriptLoaded = 'true';
            window.console.log('Stylitics Analytics: Event listeners for analytics are set up');
        }).catch(err => {
            window.console.error('Stylitics Analytics: Failed to load Stylitics Insights script:', err);
        });
});
